import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/lib/api";
const PERMS = ["enquiries", "applications"];

const TeamPanel = () => {
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", permissions: [...PERMS] });
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios
      .get(`${API}/team`, { withCredentials: true })
      .then((r) => setMembers(r.data))
      .catch(() => setMembers([]));

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/team`, form, { withCredentials: true });
      toast.success("Team member added — they sign in using Email Link on the sign-in page");
      setForm({ name: "", email: "", permissions: [...PERMS] });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add the member");
    } finally {
      setBusy(false);
    }
  };

  const togglePerm = async (m, perm) => {
    const current = m.permissions || [...PERMS];
    const next = current.includes(perm)
      ? current.filter((p) => p !== perm)
      : [...current, perm];
    try {
      await axios.patch(
        `${API}/team/${m.id}`,
        { permissions: next },
        { withCredentials: true }
      );
      toast.success("Permissions updated");
      load();
    } catch {
      toast.error("Could not update permissions");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove access for ${m.email}?`)) return;
    try {
      await axios.delete(`${API}/team/${m.id}`, { withCredentials: true });
      toast.success("Access removed");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not remove the member");
    }
  };

  const inputCls =
    "rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10";

  const PermPill = ({ active, onClick, label, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all duration-300 ${
        active
          ? "bg-navy text-white"
          : "border border-gray-200 bg-white text-fog hover:border-navy hover:text-navy"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="team-panel">
      <form
        onSubmit={add}
        className="mt-6 rounded-3xl border border-gray-100 bg-white p-7 shadow-sm"
        data-testid="team-add-form"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <input
            required
            placeholder="Member name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="team-name-input"
            className={`${inputCls} flex-1`}
          />
          <input
            required
            type="email"
            placeholder="Member email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            data-testid="team-email-input"
            className={`${inputCls} flex-1`}
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="team-add-button"
            className="flex items-center justify-center gap-2 rounded-full bg-navy px-7 py-3 font-heading text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Add Member
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-fog">Can access:</span>
          {PERMS.map((p) => (
            <PermPill
              key={p}
              label={p}
              testid={`new-member-perm-${p}`}
              active={form.permissions.includes(p)}
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  permissions: f.permissions.includes(p)
                    ? f.permissions.filter((x) => x !== p)
                    : [...f.permissions, p],
                }))
              }
            />
          ))}
        </div>
      </form>
      <p className="mt-3 text-xs text-fog">
        Members sign in with the "Email Link" option — a one-time secure link sent to their
        email. Use the access pills to choose what each member can see.
      </p>

      {members === null ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-navy" />
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {members.map((m) => (
            <div
              key={m.id}
              data-testid="team-member-card"
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-6 py-4 shadow-sm"
            >
              <div>
                <p className="font-heading text-sm font-bold text-ink">{m.name}</p>
                <p className="text-xs text-fog">{m.email}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {m.role === "admin" ? (
                  <span className="rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gold-dark">
                    Admin · Full access
                  </span>
                ) : (
                  <>
                    {PERMS.map((p) => (
                      <PermPill
                        key={p}
                        label={p}
                        testid={`perm-${m.id}-${p}`}
                        active={(m.permissions || [...PERMS]).includes(p)}
                        onClick={() => togglePerm(m, p)}
                      />
                    ))}
                    <button
                      onClick={() => remove(m)}
                      data-testid={`team-remove-${m.id}`}
                      aria-label={`Remove ${m.email}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-fog transition-all duration-300 hover:border-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamPanel;
